import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import AuthModal from './components/AuthModal';
import InstallPrompt from './components/pwa/InstallPrompt';
import { useAnalytics } from './hooks/useAnalytics';

const Home = lazy(() => import('./pages/Home'));
const Article = lazy(() => import('./pages/Article'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const WriterDashboard = lazy(() => import('./pages/WriterDashboard'));
const NewArticle = lazy(() => import('./pages/NewArticle'));
const EditArticle = lazy(() => import('./pages/EditArticle'));
const AllArticles = lazy(() => import('./pages/AllArticles'));
const Latest = lazy(() => import('./pages/Latest'));
const Auth = lazy(() => import('./pages/Auth'));
const Account = lazy(() => import('./pages/Account'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Category = lazy(() => import('./pages/Category'));
const Shop = lazy(() => import('./pages/Shop'));
const WriterProfile = lazy(() => import('./pages/WriterProfile'));
const Subscribe = lazy(() => import('./pages/Subscribe'));
const Search = lazy(() => import('./pages/Search'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const DashboardGuide = lazy(() => import('./pages/DashboardGuide'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50" role="status" aria-live="polite">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-[#B6AC7D] border-t-transparent rounded-full animate-spin mx-auto mb-4" aria-hidden="true"></div>
      <p className="text-gray-600 font-medium">Loading...</p>
      <span className="sr-only">Loading page content</span>
    </div>
  </div>
);

function App() {
  useAnalytics();
  const location = useLocation();

  useEffect(() => {
    document.body.classList.remove('page-transition-exit', 'page-transition-exit-active');
    document.body.classList.add('page-transition-enter');

    requestAnimationFrame(() => {
      document.body.classList.add('page-transition-enter-active');
    });

    const timeout = setTimeout(() => {
      document.body.classList.remove('page-transition-enter', 'page-transition-enter-active');
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-[#B6AC7D] text-white px-4 py-2 rounded-lg z-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B6AC7D]">
        Skip to main content
      </a>
      <ScrollToTop />
      <Navbar />
      <main id="main-content" tabIndex={-1}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<Search />} />
            <Route path="/news/:slug" element={<Article />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/writer" element={<WriterDashboard />} />
            <Route path="/admin/new-article" element={<NewArticle />} />
            <Route path="/writer/new-article" element={<NewArticle />} />
            <Route path="/admin/edit/:id" element={<EditArticle />} />
            <Route path="/writer/edit/:id" element={<EditArticle />} />
            <Route path="/all-articles" element={<AllArticles />} />
            <Route path="/latest" element={<Latest />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/account" element={<Account />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/category/:slug" element={<Category />} />
            <Route path="/writer/:username" element={<WriterProfile />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/guide" element={<DashboardGuide />} />
            <Route path="/admin/guide" element={<DashboardGuide />} />
            <Route path="/writer/guide" element={<DashboardGuide />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <AuthModal />
      <InstallPrompt />
    </div>
  );
}

export default App;
